import { Injectable, signal, inject, computed, OnDestroy } from "@angular/core";
import { firstValueFrom, Observable, Subject, takeUntil } from "rxjs";
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { CreateDroneReportRequest } from '../request-models/create-drone-report-request.model';
import { DroneReportListOptions } from '../request-models/drone-report-list-options.model';
import { DroneReport } from '../data-models/drone-report.model';
import { DroneReportScope } from '../data-models/drone-report-scope.model';
import { DroneReportStatus } from '../data-models/drone-report-status.model';
import { DroneReportCompleteEvent } from '../response-models/drone-report-complete-event.model';
import { DroneReportFailedEvent } from '../response-models/drone-report-failed-event.model';
import { DroneReportJob } from '../response-models/drone-report-job.model';
import { DroneReportProgressEvent } from '../response-models/drone-report-progress-event.model';
import { Page } from '../response-models/page.model';
import { DRONE_REPORT_ROLES, toCreateDroneReportRequest, toDroneReportListQuery } from '../utils/drone-report-utils';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';
import { WebSocketService } from "../services/websocket.service";
import { formatDateRange } from "src/app/shared/utils/date.utils";
import { OrganizationContextService } from "../services/organization-context.service";
import { CustomClaimService } from "../services/custom-claim.service";
import { orgPrefix } from '../utils/org-path-utils';
import { withChanges } from '../utils/model-utils';

export type DroneReportActionResult = { ok: true } | { ok: false; error: string };

/**
 * Drone reports: `/organizations/:id/drone-reports[/:id[/generate|/retry]]`
 * (hub `user-drone-reports-controller.ts`), plus their `drone-report:*` WebSocket events.
 *
 * Observable methods reject with an `ApiError`; the Promise wrappers record its message in `error`.
 */
@Injectable({
  providedIn: 'root'
})
export class DroneReportService extends BaseService<DroneReport> implements OnDestroy {
    private customClaimService = inject(CustomClaimService);
    private organizationContext = inject(OrganizationContextService);
    private webSocketService = inject(WebSocketService);
    private destroy$ = new Subject<void>();
    private isSubscribed = false;

    // ============================================
    // STATE
    // ============================================

    private reportsSignal = signal<DroneReport[]>([]);
    private loadingSignal = signal<boolean>(false);
    private errorSignal = signal<string | null>(null);
    private totalCountSignal = signal<number>(0);
    private currentPageSignal = signal<number>(1);
    private pageSizeSignal = signal<number>(10);

    readonly reports = computed(() => this.reportsSignal());
    readonly error = computed(() => this.errorSignal());

    // Computed stats
    readonly generatingReports = computed(() =>
        this.reportsSignal().filter(r => r.status === 'generating')
    );

    readonly readyReports = computed(() =>
        this.reportsSignal().filter(r => r.status === 'ready')
    );

    readonly failedReports = computed(() =>
        this.reportsSignal().filter(r => r.status === 'failed')
    );

    // ============================================
    // LIFECYCLE
    // ============================================

    constructor() {
        super('drone-reports', DroneReport);
        this.webSocketService.connectionState$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(state => {
            if (state === 'authenticated' && !this.isSubscribed) {
                this.setupWebSocketSubscriptions();
            } else if (state === 'disconnected') {
                this.isSubscribed = false;
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ============================================
    // WEBSOCKET SUBSCRIPTIONS
    // ============================================

    /**
     * Set up WebSocket subscriptions for real-time Drone report updates.
     * Listens for progress, complete, and failed events.
     * Called when WebSocket connection is authenticated.
     */
    private setupWebSocketSubscriptions(): void {
        if (this.isSubscribed) return;
        this.isSubscribed = true;

        console.log('[DroneReportService] Setting up WebSocket subscriptions for Drone report events');

        // Subscribe to progress events
        this.webSocketService.on<DroneReportProgressEvent>('drone-report:progress')
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => {
                console.log('[DroneReportService] Received drone-report:progress:', event);
                this.handleProgressEvent(event);
            });

        // Subscribe to complete events
        this.webSocketService.on<DroneReportCompleteEvent>('drone-report:complete')
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => {
                console.log('[DroneReportService] Received drone-report:complete:', event);
                this.handleCompleteEvent(event);
            });

        // Subscribe to failed events
        this.webSocketService.on<DroneReportFailedEvent>('drone-report:failed')
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => {
                console.log('[DroneReportService] Received drone-report:failed:', event);
                this.handleFailedEvent(event);
            });

        console.log('[DroneReportService] WebSocket subscriptions established');
    }

    private handleProgressEvent(event: DroneReportProgressEvent): void {
        this.patchReport(event.reportId, { status: event.status, generationProgress: event.progress });
    }

    private handleCompleteEvent(event: DroneReportCompleteEvent): void {
        this.patchReport(event.reportId, {
            status: event.status,
            reportUrl: event.url,
            recordCount: event.recordCount,
            fileSize: event.fileSize,
            generationProgress: 100
        });
    }

    private handleFailedEvent(event: DroneReportFailedEvent): void {
        this.patchReport(event.reportId, { status: event.status, errorMessage: event.error, generationProgress: 0 });
    }

    // ============================================
    // LIST
    // ============================================

    /**
     * List an organization's drone reports, one page at a time.
     * Rejects with an `ApiError`.
     */
    listReports(orgId: string, options?: DroneReportListOptions): Observable<Page<DroneReport>> {
        return this.getPage({ prefix: orgPrefix(orgId), query: toDroneReportListQuery(options) });
    }

    /**
     * Load reports into signal state. Failures set `error` instead of throwing.
     */
    async loadReports(orgId: string, options?: DroneReportListOptions): Promise<void> {
        this.loadingSignal.set(true);
        this.errorSignal.set(null);
        // Clear the previous drone's reports so they don't flash while this list loads
        this.reportsSignal.set([]);

        try {
            const page = await firstValueFrom(this.listReports(orgId, options));
            this.reportsSignal.set(page.items);
            this.totalCountSignal.set(page.paging.totalItems);
            this.currentPageSignal.set(page.paging.page);
            this.pageSizeSignal.set(page.paging.pageSize);
        } catch (error: unknown) {
            this.errorSignal.set(errorMessage(error, 'Failed to load reports'));
        } finally {
            this.loadingSignal.set(false);
        }
    }

    // ============================================
    // GET
    // ============================================

    /** One report, with a fresh pre-signed `downloadUrl` when it is ready. Rejects with an `ApiError`. */
    getReport(orgId: string, reportId: string): Observable<DroneReport | null> {
        return this.get(reportId, { prefix: orgPrefix(orgId) });
    }

    /** One report, or `null` when it is missing or the request fails. */
    async fetchReport(orgId: string, reportId: string): Promise<DroneReport | null> {
        try {
            return await firstValueFrom(this.getReport(orgId, reportId));
        } catch (error: unknown) {
            console.error('Error fetching Drone report:', ApiError.fromHttpError(error).message);
            return null;
        }
    }

    // ============================================
    // CREATE
    // ============================================

    /** Create a drone report in draft status. Rejects with an `ApiError`. */
    createReport(orgId: string, data: CreateDroneReportRequest): Observable<DroneReport | null> {
        return this.create(toCreateDroneReportRequest(data), { prefix: orgPrefix(orgId) });
    }

    /** Create a report and add it to signal state. `null` (with `error` set) on failure. */
    async createAndAddReport(orgId: string, data: CreateDroneReportRequest): Promise<DroneReport | null> {
        this.loadingSignal.set(true);
        this.errorSignal.set(null);

        try {
            const newReport = await firstValueFrom(this.createReport(orgId, data));
            if (!newReport) {
                throw new Error('Failed to create report');
            }
            this.reportsSignal.update(reports => [newReport, ...reports]);
            this.totalCountSignal.update(count => count + 1);
            return newReport;
        } catch (error: unknown) {
            this.errorSignal.set(errorMessage(error, 'Failed to create report'));
            return null;
        } finally {
            this.loadingSignal.set(false);
        }
    }

    // ============================================
    // GENERATE / RETRY
    // ============================================

    /** Queue PDF generation for a draft or failed report. Rejects with an `ApiError`. */
    generatePdf(orgId: string, reportId: string): Observable<DroneReportJob> {
        return this.request('POST', { prefix: orgPrefix(orgId), id: reportId, path: 'generate' }, toJob, {});
    }

    /** Trigger PDF generation and mark the report generating. `false` (with `error` set) on failure. */
    async triggerPdfGeneration(orgId: string, reportId: string): Promise<boolean> {
        return this.startGenerating(this.generatePdf(orgId, reportId), reportId, 'Failed to start generation');
    }

    /** Retry a failed report's generation. Rejects with an `ApiError`. */
    retryReport(orgId: string, reportId: string): Observable<DroneReportJob> {
        return this.request('POST', { prefix: orgPrefix(orgId), id: reportId, path: 'retry' }, toJob, {});
    }

    /** Retry a failed report and mark it generating. `false` (with `error` set) on failure. */
    async retryFailedReport(orgId: string, reportId: string): Promise<boolean> {
        return this.startGenerating(this.retryReport(orgId, reportId), reportId, 'Failed to retry generation');
    }

    // ============================================
    // DELETE
    // ============================================

    /** Soft-delete a drone report. Rejects with an `ApiError`. */
    deleteReport(orgId: string, reportId: string): Observable<void> {
        return this.delete(reportId, { prefix: orgPrefix(orgId) });
    }

    /** Delete a report and remove it from signal state. `false` (with `error` set) on failure. */
    async deleteAndRemoveReport(orgId: string, reportId: string): Promise<boolean> {
        try {
            await firstValueFrom(this.deleteReport(orgId, reportId));
            this.reportsSignal.update(reports => reports.filter(r => r.id !== reportId));
            this.totalCountSignal.update(count => Math.max(0, count - 1));
            return true;
        } catch (error: unknown) {
            this.errorSignal.set(errorMessage(error, 'Failed to delete report'));
            return false;
        }
    }

    // ============================================
    // DISPLAY HELPERS
    // ============================================

    /**
     * Get Bootstrap icon class for status
     */
    getStatusIcon(status: DroneReportStatus): string {
        switch (status) {
            case 'draft': return 'bi-file-earmark';
            case 'generating': return 'bi-arrow-repeat';
            case 'ready': return 'bi-check-circle';
            case 'submitted': return 'bi-send-check';
            case 'failed': return 'bi-exclamation-triangle';
            default: return 'bi-file-earmark';
        }
    }

    /**
     * Get human-readable status text
     */
    getStatusText(status: DroneReportStatus): string {
        switch (status) {
            case 'draft': return 'Draft';
            case 'generating': return 'Generating...';
            case 'ready': return 'Ready';
            case 'submitted': return 'Submitted';
            case 'failed': return 'Failed';
            default: return status;
        }
    }

    /**
     * Format date range for display, e.g. "Oct 1, 2026 - Oct 15, 2026"
     */
    formatDateRange(startDate: string, endDate: string, options: { format?: Intl.DateTimeFormatOptions; separator?: string } = {}, range: boolean = true): string | null {
        return formatDateRange(startDate, endDate, options, range);
    }

    /**
     * A report's dates for display: a range for full history, a single date
     * otherwise, or '' when the report has none
     */
    formatReportDates(report: Pick<DroneReport, 'startDate' | 'endDate' | 'scope'>): string {
        return formatDateRange(report.startDate, report.endDate, {}, report.scope === 'full_history') ?? '';
    }

    /**
     * Human-readable report scope
     */
    getScopeLabel(scope: DroneReportScope): string {
        return scope === 'full_history' ? 'Full History' : 'Single Incident';
    }

    /**
     * Clear error state
     */
    clearError(): void {
        this.errorSignal.set(null);
    }

    // ============================================
    // PRIVATE HELPERS
    // ============================================

    /** Marks a report generating once its job is queued; records the hub's error otherwise. */
    private async startGenerating(job: Observable<DroneReportJob>, reportId: string, fallback: string): Promise<boolean> {
        try {
            await firstValueFrom(job);
            this.patchReport(reportId, { status: 'generating', generationProgress: 0, errorMessage: null });
            return true;
        } catch (error: unknown) {
            this.errorSignal.set(errorMessage(error, fallback));
            return false;
        }
    }

    /** Replaces one cached report with a patched copy (a fresh instance, so signal readers see the change). */
    private patchReport(reportId: string, changes: Partial<DroneReport>): void {
        this.reportsSignal.update(reports =>
            reports.map(report => (report.id === reportId ? withChanges(report, changes) : report))
        );
    }

    canManageReports(): boolean {
        const orgId = this.organizationContext.currentOrganizationId();
        return !!orgId && this.customClaimService.hasAnyOrgRole(DRONE_REPORT_ROLES, orgId);
    }

    // ============================================
    // ACTION METHODS
    // ============================================

    async startReport(request: CreateDroneReportRequest): Promise<DroneReport | null> {
        const orgId = this.organizationContext.currentOrganizationId();
        if (!orgId) return null;

        const report = await this.createAndAddReport(orgId, request);
        if (!report) return null;

        const reportStarted = await this.triggerPdfGeneration(orgId, report.id);
        return reportStarted ? report : null;
    }

    async getDownloadUrl(report: DroneReport): Promise<string | null> {
        if (report.status !== 'ready' && report.status !== 'submitted') return null;

        const orgId = this.organizationContext.currentOrganizationId();
        if (!orgId) return null;

        // Fetch fresh report data to get a valid pre-signed URL
        const freshReport = await this.fetchReport(orgId, report.id);

        // Use downloadUrl (pre-signed URL) for download
        if (!freshReport?.downloadUrl) {
            this.errorSignal.set('The download link could not be created. Please try again.');
            return null;        
        }
        return freshReport.downloadUrl;
    }

    async removeReport(report: DroneReport): Promise<DroneReportActionResult> {
        // Cannot delete while generating
        if (report.status === 'generating') {
            return { ok: false, error: 'Cannot delete a report while it is generating' };
        }

        const orgId = this.organizationContext.currentOrganizationId();
        if (!orgId) {
            return { ok: false, error: 'No organization selected' };
        }

        const success = await this.deleteAndRemoveReport(orgId, report.id);
        if (success) {
            return { ok: true };
        }

        const error = this.error() ?? 'Failed to delete report';
        this.clearError();
        return { ok: false, error };
    }

}


/** The hub's message for a failed request, else `fallback`. */
function errorMessage(error: unknown, fallback: string): string {
    return ApiError.fromHttpError(error).errors[0] ?? fallback;
}

function toJob(data: unknown): DroneReportJob {
    return { jobId: isRecord(data) ? nullableString(data['jobId']) ?? null : null };
}

